import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import {
  assetService,
  blockService,
  domainService,
  pageService,
  letterIconUrl,
  setFavicon,
  siteDesignAttributes,
  siteDesignFrom,
  siteIconLetter,
  type SiteDesign,
} from "../features";
import { SitePageView } from "../features/editor";
import type { EditorAsset, EditorBlock, EditorPage } from "../features/editor";
import { config } from "../lib/config";
import { Button, Card, CenteredPage, Wordmark, buttonClass } from "../components/ui";

const currentPath = () => {
  const path = window.location.pathname;
  return path.length > 1 ? path.replace(/\/+$/, "") : "/";
};

const pagePath = (page: EditorPage) => (page.is_home ? "/" : `/${page.slug}`);

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "not-found" }
  | {
      status: "ready";
      siteName: string;
      siteDescription: string | null;
      design: SiteDesign;
      iconUrl: string | null;
      pages: EditorPage[];
      assetsById: Map<string, EditorAsset>;
    };

type PublicSitePageProps = {
  hostname: string;
};

const SiteNotFound = () => (
  <main className="flex min-h-screen flex-col items-center justify-center bg-hazumu-canvas px-6 text-center">
    <Wordmark size="sm" />
    <h1 className="mt-6 font-lora text-[28px] font-semibold leading-[1.2] text-hazumu-ink">
      There&rsquo;s no site here yet
    </h1>
    <p className="mt-3 max-w-sm font-sans text-[13px] leading-[1.6] text-hazumu-muted">
      This address isn&rsquo;t connected to a published Hazumu site. It may have
      moved, or the link could be wrong.
    </p>
    <a
      className={`mt-8 font-sans ${buttonClass({ variant: "primary", size: "lg", pill: true })}`}
      href={`https://${config.hazumuRootDomain}`}
    >
      Make your own site
    </a>
  </main>
);

export const PublicSitePage = ({ hostname }: PublicSitePageProps) => {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [path, setPath] = useState(currentPath);
  const [blocksByPage, setBlocksByPage] = useState<
    Record<string, EditorBlock[]>
  >({});
  const [blocksError, setBlocksError] = useState<{
    pageId: string;
    message: string;
  } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const requestedPages = useRef(new Set<string>());

  const navigate = useCallback((to: string) => {
    window.history.pushState(null, "", to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  const linkHandler = useCallback(
    (to: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return;
      }
      event.preventDefault();
      navigate(to);
    },
    [navigate],
  );

  useEffect(() => {
    const onPopState = () => setPath(currentPath());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // The site's canonical hostname — its primary custom domain when one is
  // set. The edge worker normally 301s non-primary hostnames before this code
  // runs; for anything that bypasses the edge we emit the canonical link and
  // do the redirect client-side instead.
  const [canonicalHost, setCanonicalHost] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    domainService
      .canonicalHostname(hostname)
      .then((canonical) => {
        if (active) setCanonicalHost(canonical ?? hostname);
      })
      .catch(() => {
        if (active) setCanonicalHost(hostname);
      });
    return () => {
      active = false;
    };
  }, [hostname]);

  useEffect(() => {
    if (!canonicalHost) return;
    if (canonicalHost !== hostname) {
      window.location.replace(
        `https://${canonicalHost}${window.location.pathname}${window.location.search}${window.location.hash}`,
      );
      return;
    }
    let link = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `https://${canonicalHost}${path}`;
  }, [canonicalHost, hostname, path]);

  const loadSite = useCallback(async (): Promise<LoadState> => {
    const resolved = await domainService.getPublishedSite(hostname);
    if (!resolved) return { status: "not-found" };
    const [pages, assets] = await Promise.all([
      pageService.list(resolved.version.id),
      assetService.list(resolved.site.id),
    ]);
    const design = siteDesignFrom(resolved.version.settings);
    const icon = assets.find((asset) => asset.id === design.icon);
    return {
      status: "ready",
      siteName: resolved.version.name,
      siteDescription: resolved.version.description,
      design,
      iconUrl: icon ? assetService.getPublicUrl(icon.storage_path) : null,
      // Menu visibility does not control whether a published page can be opened.
      pages,
      assetsById: new Map(assets.map((asset) => [asset.id, asset])),
    };
  }, [hostname]);

  useEffect(() => {
    let active = true;
    void loadSite()
      .then((next) => {
        if (active) setState(next);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setState({
            status: "error",
            message:
              loadError instanceof Error
                ? loadError.message
                : "Could not load this site.",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [loadSite, reloadKey]);

  const pages = state.status === "ready" ? state.pages : [];
  const slug = path === "/" ? "" : path.slice(1);
  const homePage = pages.find((page) => page.is_home) ?? pages[0];
  const currentPage = slug
    ? pages.find((page) => page.slug === slug)
    : homePage;

  const currentPageId = currentPage?.id;
  useEffect(() => {
    const requested = requestedPages.current;
    if (!currentPageId || requested.has(currentPageId)) return;
    requested.add(currentPageId);
    let active = true;
    let settled = false;
    void blockService
      .list(currentPageId)
      .then((loaded) => {
        settled = true;
        if (active) {
          setBlocksByPage((current) => ({
            ...current,
            [currentPageId]: loaded,
          }));
        }
      })
      .catch((loadError: unknown) => {
        settled = true;
        if (active) {
          requested.delete(currentPageId);
          setBlocksError({
            pageId: currentPageId,
            message:
              loadError instanceof Error
                ? loadError.message
                : "Could not load this page.",
          });
        }
      });
    return () => {
      active = false;
      if (!settled) requested.delete(currentPageId);
    };
  }, [currentPageId]);

  useEffect(() => {
    if (state.status === "ready") {
      document.title =
        currentPage && !currentPage.is_home
          ? `${currentPage.title} — ${state.siteName}`
          : state.siteName;
    } else if (state.status === "not-found") {
      document.title = "Site not found";
    }
  }, [state, currentPage]);

  const ready = state.status === "ready" ? state : null;
  const theme = ready?.design.theme;
  const accent = ready?.design.accent;
  const iconUrl = ready?.iconUrl;
  const iconLetter = ready ? siteIconLetter(ready.siteName) : "";
  // The body carries the design too, for overscroll and the site menu, and
  // gives the letter icon its colours.
  useEffect(() => {
    if (!theme || !accent) return;
    const attributes = siteDesignAttributes({ theme, accent });
    const body = document.body;
    body.dataset.siteTheme = attributes["data-site-theme"];
    if (attributes["data-site-accent"])
      body.dataset.siteAccent = attributes["data-site-accent"];
    let restore: (() => void) | undefined;
    let active = true;
    if (iconUrl) restore = setFavicon(iconUrl);
    else
      void letterIconUrl(iconLetter, body).then((href) => {
        if (active && href) restore = setFavicon(href);
      });
    return () => {
      active = false;
      restore?.();
      delete body.dataset.siteTheme;
      delete body.dataset.siteAccent;
    };
  }, [theme, accent, iconUrl, iconLetter]);

  if (state.status === "loading") {
    return (
      <CenteredPage>
        <p className="font-sans text-sm text-hazumu-muted">Loading…</p>
      </CenteredPage>
    );
  }

  if (state.status === "error") {
    return (
      <CenteredPage>
        <Card variant="hero" className="w-full max-w-md text-center">
          <h1 className="font-lora text-2xl font-semibold">
            We couldn&rsquo;t load this site
          </h1>
          <p className="mt-3 font-sans text-sm leading-6 text-hazumu-muted">
            {state.message}
          </p>
          <Button
            variant="primary"
            size="lg"
            className="mt-6 font-sans"
            onClick={() => {
              setState({ status: "loading" });
              setReloadKey((key) => key + 1);
            }}
          >
            Try again
          </Button>
        </Card>
      </CenteredPage>
    );
  }

  if (state.status === "not-found") return <SiteNotFound />;

  const { siteName, siteDescription, assetsById } = state;
  const blocks = currentPage ? blocksByPage[currentPage.id] : undefined;
  const pageBlocksError =
    blocksError && blocksError.pageId === currentPageId
      ? blocksError.message
      : "";
  return (
    <SitePageView
      design={state.design}
      siteName={siteName}
      siteDescription={siteDescription}
      pages={pages}
      currentPage={currentPage}
      blocks={blocks}
      blocksError={pageBlocksError}
      assetsById={assetsById}
      href={pagePath}
      onNavigate={(page) => navigate(pagePath(page))}
      notFound={
        <article className="mx-auto flex max-w-4xl flex-col items-start px-5 py-16 sm:px-8">
          <h1 className="site-heading heading-size-30">Page not found</h1>
          <p className="mt-3 text-sm leading-6 text-site-muted">
            That page doesn&rsquo;t exist on {siteName}.
          </p>
          <a
            className="mt-6 rounded-site-pill bg-site-text px-5 py-2.5 text-[13px] font-semibold text-site-bg transition hover:bg-site-accent"
            href="/"
            onClick={linkHandler("/")}
          >
            Back to {siteName}
          </a>
        </article>
      }
    />
  );
};
